<template>
  <v-navigation-drawer
    app
    color="#F7F7F7"
    clipped
    v-model="showDrawerInternal"
    class="pt-5"
    :mini-variant="$vuetify.breakpoint.width < 1400 && !$vuetify.breakpoint.mobile"
  >
    <v-menu
      v-for="menuItem in menuItems"
      :key="menuItem.route + '-' + navigationVersion"
      :open-on-hover="menuItem.children.length > 0"
      :open-on-click="false"
      right
      offset-x
    >
      <template v-slot:activator="{ on, attrs }">
        <v-list-item
          v-bind="attrs"
          v-on="on"
          :text="!isRouteActive(menuItem)"
          :title="menuItem.text"
          color="primary"
          :input-value="isRouteActive(menuItem)"
          @click="pushToRoute(menuItem.route)"
        >
          <v-list-item-icon><v-icon left>{{ menuItem.icon }}</v-icon></v-list-item-icon>
          <v-list-item-title>{{ menuItem.text }}</v-list-item-title>
          <v-list-item-action v-if="menuItem.children.length > 0"><v-icon right>mdi-chevron-right</v-icon></v-list-item-action>
        </v-list-item>
      </template>

      <v-list v-if="menuItem.children.length > 0">
        <v-list-item
          v-for="childMenuItem in menuItem.children"
          :key="childMenuItem.route"
          :title="childMenuItem.text"
          color="primary"
          :input-value="isRouteActive(childMenuItem)"
          @click="pushToRoute(childMenuItem.route)"
        >
          <v-list-item-icon><v-icon>{{ childMenuItem.icon }}</v-icon></v-list-item-icon>
          <v-list-item-title>{{ childMenuItem.text }}</v-list-item-title>
        </v-list-item>
      </v-list>

    </v-menu>
  </v-navigation-drawer>
</template>

<script>
import {RouterUtils} from './utils/router-utils';
import {MainUtils} from '@/utils/main-utils';
import {getRoute} from './router/utils';

export default {
  name: 'NavigationDrawer',
  mixins: [
    RouterUtils,
    MainUtils
  ],
  props: {
    showDrawer: {
      type: Boolean,
      default () {
        return false;
      }
    }
  },
  data: () => ({
    currentRoute: null,
    navigationVersion: 1,
    showDrawerInternal: null
  }),
  computed: {
    allMenuItems () {
      return [
        {
          route: 'Dashboard',
          text: this.$t('dashboard.dashboard'),
          icon: 'mdi-view-dashboard'
        },
        {
          route: 'ObjectGroupsPage',
          text: this.$tc('objectGroup.objectGroup', 0),
          icon: 'mdi-home-group',
          children: [
            {
              route: 'ObjectGroupsSortPage',
              text: this.$tc('objectGroup.sortObjectGroups', 0),
              icon: 'mdi-sort-ascending',
            }
          ]
        },
        {
          route: 'ObjectsPage',
          text: this.$tc('object.object', 0),
          icon: 'mdi-home'
        },
        {
          route: 'Bookings',
          text: this.$tc('booking.booking', 0),
          icon: 'mdi-notebook-multiple',
          children: [
            {
              route: 'BookingsCheckIn',
              text: this.$tc('booking.checkin', 0),
              icon: 'mdi-car-arrow-left',
            },
            {
              route: 'BookingsCheckOut',
              text: this.$tc('booking.checkout', 0),
              icon: 'mdi-car-arrow-right',
            },
            {
              route: 'BookingsOnlyNew',
              text: this.$tc('booking.newBookings', 0),
              icon: 'mdi-alert-decagram',
            }
          ]
        },
        {
          route: 'Settlements',
          text: this.$tc('settlement.settlement', 0),
          icon: 'mdi-text-box-multiple-outline'
        },
        {
          route: 'ObjectMiscs',
          text: this.$tc('misc.miscsObject', 0),
          icon: 'mdi-note-plus'
        },
        {
          route: 'Transactions',
          text: this.$tc('transaction.transaction', 0),
          icon: 'mdi-credit-card-refund'
        },
        {
          route: 'Surveys',
          text: this.$tc('survey.surveys', 0),
          icon: 'mdi-clipboard-pulse'
        },
        {
          route: 'Guests',
          text: this.$tc('guest.guests', 0),
          icon: 'mdi-account-cowboy-hat'
        },
        {
          route: 'Users',
          text: this.$tc('user.users', 0),
          icon: 'mdi-account-group'
        },
        {
          route: 'MiscAccounts',
          text: this.$tc('misc.accounts', 0),
          icon: 'mdi-cash'
        },
        {
          route: 'ImportStayin',
          text: this.$t('import.importStayin'),
          icon: 'mdi-database-import'
        }
      ];
    },
    menuItems() {
      let allowedMenuItems = this.filterByPermission(this.allMenuItems);

      let menuItems = [];
      let menuItemCount = allowedMenuItems.length;

      for(let menuItem of allowedMenuItems) {
        menuItems.push(menuItem);
        menuItem.checkSubRoute = true;

        if(menuItemCount <= 5) {
          if(menuItem.children.length > 0) {
            for(let childMenuItem of menuItem.children) {
              menuItem.checkSubRoute = false;
              menuItems.push(childMenuItem);
              menuItemCount++;
            }
            menuItem.children = [];
          }
        }
      }

      // fix wrong order of v-menu even using :key
      // maybe https://github.com/vuetifyjs/vuetify/issues/9753
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.navigationVersion = this.navigationVersion + 1;

      return menuItems;
    }
  },
  methods: {
    filterByPermission(routes) {
      return routes
          // clone to allow manipulations
          .map(menuItem => Object.assign({}, menuItem))
          .filter(menuItem => {
            let route = getRoute(menuItem.route);
            if (route?.meta?.permission) {
              return this.hasAnyPermission(route?.meta?.permission);
            }
            return true;
          })
          .map(menuItem => {
            if(Array.isArray(menuItem.children)) {
              menuItem.children = this.filterByPermission(menuItem.children);
            } else {
              menuItem.children = []
            }
            return menuItem;
          })
          ;
    },
    isRouteActive (menuItem) {
      let route = menuItem.route;
      let isSubRoute;
      try {
        let routeHref = this.$router.resolve({name: route}).href;

        isSubRoute = this.currentRoute && routeHref && this.currentRoute.path.startsWith(routeHref);
      } catch (e) {
        console.warn(e);
      }
      return this.currentRoute && this.currentRoute.name === (route) || menuItem.checkSubRoute && isSubRoute;
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler (to) {
        this.currentRoute = to;
      }
    },
    showDrawer (showDrawer) {
      this.showDrawerInternal = showDrawer;
    },
    showDrawerInternal (showDrawerInternal) {
      this.$emit('update:show-drawer', showDrawerInternal);
    }
  }
}
</script>
